/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import Tbl from '@appblocks/codegeneration/Libraries/Tbl';
import TableItem from '@appblocks/codegeneration/SettingsConfigurations/TableItem';

export const getCodeBlocks = (feature: BaseFeature): CodeBlock[] => {
    const blocks: Array<CodeBlock> = [];
    const project = feature.project;
    const IP = feature.getSettingValue('IP');
    const GATEWAY = feature.getSettingValue('GI');
    const NETMASK = feature.getSettingValue('NM');
    const DHCP = feature.getSettingValue('DHCP');

    blocks.push({
        location: 'VARIABLE_DECLARATIONS',
        blockType: BlockType.CODE,
        template: `
static struct net_mgmt_event_callback ipv4_cb;
static struct net_mgmt_event_callback net_l4_mgmt_cb;
static bool dhcp_enabled = false;
`,
    });

    blocks.push({
        location: 'GLOBAL_INCLUDES',
        blockType: BlockType.CODE,
        template: `
#include <zephyr/net/net_if.h>
#include <zephyr/net/net_event.h>

#include <zephyr/net/socket.h>

#include <zephyr/net/net_pkt.h>
#include <zephyr/net/net_mgmt.h>
#include <zephyr/net/conn_mgr_monitor.h>
`,
    });

    blocks.push({
        location: 'GLOBAL_DEFINES',
        blockType: BlockType.CODE,
        template: `
#define NET_L4_EVENT_MASK (NET_EVENT_L4_CONNECTED | NET_EVENT_L4_DISCONNECTED | NET_EVENT_IPV4_ADDR_ADD)
#define NET_IPV4_EVENT_MASK (NET_EVENT_IPV4_ADDR_ADD)
`,
    });

    blocks.push({
        location: 'PRJ_CONF',
        blockType: BlockType.CODE,
        template: `
CONFIG_INIT_STACKS=y

CONFIG_NETWORKING=y
CONFIG_NET_IPV4=y
CONFIG_NET_IPV6=n
CONFIG_NET_UDP=y
CONFIG_NET_TCP=y

CONFIG_NET_SOCKETS=y
# CONFIG_NET_LOG=y

# Use DHCP for IPv4
CONFIG_NET_DHCPV4=y

CONFIG_NET_CONFIG_AUTO_INIT=y
# Or assign a static IP address (useful for testing)
# Following line must be enabled, otherwise WiFi connection fails with -1.
# CONFIG_NET_CONFIG_SETTINGS=y
# CONFIG_NET_CONFIG_NEED_IPV4=y
# CONFIG_NET_CONFIG_MY_IPV4_ADDR=${IP}
# CONFIG_NET_CONFIG_MY_IPV4_GW=${GATEWAY}
# CONFIG_NET_CONFIG_MY_IPV4_NETMASK=${NETMASK}

# Enabling logging can be helpful for debugging
#CONFIG_NET_IPV6_LOG_LEVEL_DBG=y

CONFIG_NET_TX_STACK_SIZE=2048
CONFIG_NET_RX_STACK_SIZE=2048

CONFIG_NET_PKT_RX_COUNT=20
CONFIG_NET_PKT_TX_COUNT=20

CONFIG_NET_BUF_RX_COUNT=10
CONFIG_NET_BUF_TX_COUNT=30

CONFIG_NET_MAX_CONTEXTS=5

# Added to prevent error with missing #include <ethernet/eth_stats.h> for esp_wifi_drv.c
# Adding this line will cause CMAKE Warning: No SOURCES given to Zephyr library: drivers__ethernet
# Just ignore.
CONFIG_NET_L2_ETHERNET=y

# Enable net conn manager
CONFIG_NET_CONNECTION_MANAGER=y

CONFIG_ENTROPY_GENERATOR=y
CONFIG_TEST_RANDOM_GENERATOR=y
`,
    });

    blocks.push({
        location: 'FUNCTION_DEFINITIONS',
        template: `
static void handle_ipv4_result(struct net_if *iface)
{
    int i = 0;

    for (i = 0; i < NET_IF_MAX_IPV4_ADDR; i++)
    {

        char buf[NET_IPV4_ADDR_LEN];

        

        LOG_INF("IPv4 address: %s\\n",
            net_addr_ntop(AF_INET,
                            &iface->config.ip.ipv4->unicast[i].ipv4.address.in_addr,
                            buf, sizeof(buf)));
        LOG_INF("Subnet: %s\\n",
            net_addr_ntop(AF_INET,
                            &iface->config.ip.ipv4->unicast[i].netmask,
                            buf, sizeof(buf)));
        LOG_INF("Router: %s\\n",
            net_addr_ntop(AF_INET,
                            &iface->config.ip.ipv4->gw,
                            buf, sizeof(buf)));

        // determine if the ipv4 address is dhcp
        if (dhcp_enabled) {
            if (iface->config.ip.ipv4->unicast[i].ipv4.addr_type != NET_ADDR_DHCP)
            {
                continue;
            }
        }
    }

    //k_sem_give(&ipv4_address_obtained);
}        

static void net_evt_handler(struct net_mgmt_event_callback *cb,
        uint32_t mgmt_event, struct net_if *iface)
{
    LOG_INF("L4 event handler called with event %d", mgmt_event);
    switch (mgmt_event) {
    case NET_EVENT_L4_CONNECTED:
        //k_sem_give(&net_conn_sem);
        LOG_INF("Network connectivity up!");
        handle_ipv4_result(iface);
        break;
    case NET_EVENT_IPV4_ADDR_ADD:
        handle_ipv4_result(iface);
        break;
    case NET_EVENT_L4_DISCONNECTED:
        LOG_INF("Network connectivity down!");
        break;
    default:
        break;
    }
}
`,
    });

    blocks.push({
        location: 'boot',
        blockType: BlockType.CODE,
        template: `
dhcp_enabled = ${DHCP === '1' ? 'true' : 'false'};

struct net_if *iface = net_if_get_default();

net_mgmt_init_event_callback(&net_l4_mgmt_cb, &net_evt_handler, NET_L4_EVENT_MASK);
net_mgmt_add_event_callback(&net_l4_mgmt_cb);

net_mgmt_init_event_callback(&ipv4_cb, &net_evt_handler, NET_IPV4_EVENT_MASK);
net_mgmt_add_event_callback(&ipv4_cb);

struct in_addr addr4;
net_addr_pton(AF_INET, string(${GATEWAY}).c_str(), &addr4);
net_if_ipv4_set_gw(iface, &addr4);
net_addr_pton(AF_INET, string(${NETMASK}).c_str(), &addr4);
net_if_ipv4_set_netmask_by_addr(iface, &addr4, &addr4);

net_addr_pton(AF_INET, string(${IP}).c_str(), &addr4);
net_if_ipv4_addr_add(iface, &addr4, NET_ADDR_OVERRIDABLE, 0);

LOG_INF("Bringing up network..");

if (dhcp_enabled) {
    net_dhcpv4_start(iface);
} else {
    net_dhcpv4_stop(iface);
}
`,
    });


    return blocks;
};
